import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { projects } from "@/data/projects";
import BrowserMockup from "./BrowserMockup";
import Reveal from "./Reveal";

export function ProjectCard({ p, index }) {
  const { t, link, lang } = useLang();
  return (
    <Reveal delay={index * 0.08}>
      <Link
        to={link(`/projects/${p.slug}`)}
        data-testid={`project-card-${p.slug}`}
        className="group block h-full rounded-2xl border border-ink/10 bg-white p-4 sm:p-5 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-dblue/30 hover:shadow-[0_30px_70px_-30px_rgba(7,17,31,0.4)]"
      >
        <div className="overflow-hidden rounded-lg transition-transform duration-500 group-hover:scale-[1.015]">
          <BrowserMockup img={p.img} url={p.url} name={p.name} />
        </div>
        <div className="px-2 pt-5 pb-2">
          <p className="text-[10px] font-semibold tracking-[0.22em] text-dblue">{p.category[lang]}</p>
          <div className="mt-2 flex items-center justify-between gap-3">
            <h3 className="font-display text-xl font-bold tracking-tight text-ink">{p.name}</h3>
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-ink/15 text-ink transition-colors duration-300 group-hover:bg-dblue group-hover:border-dblue group-hover:text-white">
              <ArrowUpRight className="h-4 w-4" />
            </span>
          </div>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-2">{p.desc[lang]}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {p.tags.map((tag) => (
              <span key={tag} className="rounded-full bg-mist px-3 py-1 text-[11px] font-medium text-slate-600">{tag}</span>
            ))}
          </div>
          <p className="mt-4 text-sm font-semibold text-dblue">{t("work.cta")} →</p>
        </div>
      </Link>
    </Reveal>
  );
}

export default function ProjectShowcase() {
  const { t, link } = useLang();
  return (
    <section data-testid="work-section" className="bg-white text-ink py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="text-xs font-semibold tracking-[0.25em] text-dblue">{t("work.eyebrow")}</p>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-2xl">{t("work.title")}</h2>
          <p className="mt-4 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed">{t("work.sub")}</p>
        </Reveal>
        <div className="mt-14 grid gap-8 md:grid-cols-2">
          {projects.map((p, i) => (
            <ProjectCard key={p.slug} p={p} index={i} />
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-12 text-center">
            <Link to={link("/projects")} data-testid="work-all-cta" className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-7 py-3.5 text-sm font-semibold text-ink transition-colors duration-300 hover:bg-ink hover:text-white">
              {t("work.allCta")} <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
